import { ComponentProps, useCallback, useContext, useState } from 'react';
import { ButtonWithIcon } from 'src/components/Buttons';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useCatalogMetricsContext } from 'src/contexts/CatalogMetricsContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { vars } from 'src/core/themes';
import { ButtonProps } from 'src/core/ui';
import { ChangeOfficeDialog } from 'src/dialogs/ChangeOfficeDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { ModalProps } from 'src/modals/Modal';
import { SplitListingModal } from 'src/modals/SplitListing';
import { LocationIcon } from 'src/svgs/LocationIcon';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ListingClient,
  ListingDetailDataField,
  ListingDetails,
  ListingMetrics,
  Office,
} from 'src/WebApiController';

export const LaunchChangeOffice = ({
  listingId,
  currentOfficeId,
  variant,
  iconOnlyMode,
  modalSize,
  ...buttonProps
}: {
  listingId: number;
  currentOfficeId?: number | null;
  variant?: ButtonProps['variant'];
  iconOnlyMode?: boolean;
  disabled?: boolean;
  modalSize?: ModalProps['size'];
} & ComponentProps<typeof SplitListingModal>) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const {
    eventsExpansion: { refreshExpandedListItems },
  } = useCatalogDataContext();
  const { refreshMetrics } = useCatalogMetricsContext<ListingMetrics>();
  const { showErrorDialog } = useClientLogContext();

  const { posEntity: listing, setActivePosEntity } =
    useActivePosEntityContext<ListingDetails>();
  const { updateModal } = useContext(ModalContext);

  const changeOfficeDialog = useBasicDialog();
  const [isLoading, setIsLoading] = useState(false);

  const onChangeOffice = useCallback(
    async (office: Office) => {
      setIsLoading(true);
      return await tryInvokeApi(
        async () => {
          const result = await new ListingClient(
            activeAccountWebClientConfig
          ).changeListingOffice({
            listingId,
            officeId: office.officeId,
          });
          if (result?.data) {
            refreshExpandedListItems();
            refreshMetrics?.();
            // Navigate to the new listing that was created
            await setActivePosEntity(result.data, undefined, true, [
              ListingDetailDataField.Basic,
            ]);
            // Update the modal's deep link so the URL reflects the new listing ID
            updateModal({ deepLinkValue: result.data });
          } else {
            showErrorDialog('ListingClient.changeListingOffice', {
              message: result?.message,
              status: result?.status,
            });
          }
        },
        (error) => {
          showErrorDialog('ListingClient.changeListingOffice', error);
        },
        () => {
          setIsLoading(false);
          changeOfficeDialog.closeDialog();
        }
      );
    },
    [
      activeAccountWebClientConfig,
      changeOfficeDialog,
      listingId,
      refreshExpandedListItems,
      refreshMetrics,
      setActivePosEntity,
      showErrorDialog,
      updateModal,
    ]
  );

  const onClickHandler = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      changeOfficeDialog.launchDialog();
    },
    [changeOfficeDialog]
  );

  return (
    <>
      <ButtonWithIcon
        {...buttonProps}
        variant={variant}
        textColor={variant === 'textPlain' ? 'strong' : 'brand'}
        style={variant === 'textPlain' ? { width: '100%' } : undefined}
        justifyContent="space-between"
        iconOnlyMode={iconOnlyMode}
        onClick={onClickHandler}
        textContentId={ContentId.ChangeOffice}
        icon={
          <LocationIcon
            withHoverEffect
            fill={
              iconOnlyMode || (variant && variant !== 'regular')
                ? variant === 'textPlain'
                  ? vars.color.textStrong
                  : vars.color.textBrand
                : vars.color.textInverted
            }
            size={vars.iconSize.m}
          />
        }
      />
      <ChangeOfficeDialog
        {...changeOfficeDialog.dialogProps}
        currentOfficeId={currentOfficeId}
        onCancel={changeOfficeDialog.closeDialog}
        onOkay={onChangeOffice}
        isLoading={isLoading}
        size={modalSize}
      />
    </>
  );
};
